/**
 * Shared VAT-derivation helpers for the expense booking UI (M13a-0).
 *
 * These three pure functions re-compute the effective VAT figures when the user
 * overrides `taxScheme` or `vatRate` in a confirmation modal. They were extracted
 * verbatim from `ExpenseBookingModal.tsx` so a second receipt-scan component
 * (M13a-4) can reuse the exact same logic instead of copying it — eliminating the
 * duplicate-definition trap.
 *
 * IMPORTANT: behaviour-identical to the original modal-scope helpers. The §17
 * Gutschrift path (negative cents, `!== 0` deductibility) is preserved exactly.
 *
 * @module expenseDeriveShared
 */

import { TaxScheme, ExpenseVatBreakdownEntry } from '../../types/expenseTypes';

/**
 * Re-computes VAT-owed cents when the user overrides taxScheme or vatRate.
 *
 * - §13b / IGL: self-computed = netCents × rate / 100 (V-4)
 * - standard: taken from original draft (document value)
 * - all others: 0
 */
export function computeEffectiveVatOwedCents(
  localTaxScheme: TaxScheme,
  localVatRate: 19 | 7,
  netCents: number,
  originalVatOwedCents: number,
): number {
  if (localTaxScheme === 'reverse_charge_13b' || localTaxScheme === 'intra_community_6a') {
    return Math.round((netCents * localVatRate) / 100);
  }
  if (localTaxScheme === 'standard') {
    return originalVatOwedCents;
  }
  return 0;
}

/**
 * Derives vatDeductible flag given the current modal state.
 * Mirrors §3.1 / C-3 from architecture-m4.md — fail-closed.
 */
export function computeEffectiveVatDeductible(
  localTaxScheme: TaxScheme,
  effectiveVatOwedCents: number,
  completenessStatus: 'complete' | 'incomplete' | 'needs_review',
  userIsFullyVatDeductible: boolean,
): boolean {
  if (completenessStatus !== 'complete') return false;
  // !== 0 (not > 0): a 381 Gutschrift carries a negative vatOwed; the §17 UStG
  // input-tax correction is deductible too (negative). Mirrors expenseTax.ts §3.1.
  if (localTaxScheme === 'standard' && effectiveVatOwedCents !== 0) return true;
  if (
    (localTaxScheme === 'reverse_charge_13b' || localTaxScheme === 'intra_community_6a') &&
    userIsFullyVatDeductible
  ) {
    return true;
  }
  return false;
}

/**
 * Recomputes per-line vatBreakdown entries when taxScheme or vatRate changes.
 */
export function computeEffectiveBreakdown(
  breakdown: ExpenseVatBreakdownEntry[],
  localTaxScheme: TaxScheme,
  localVatRate: 19 | 7,
  effectiveVatDeductible: boolean,
): ExpenseVatBreakdownEntry[] {
  const isReverseCharge =
    localTaxScheme === 'reverse_charge_13b' || localTaxScheme === 'intra_community_6a';
  return breakdown.map((b) => {
    let lineVatOwedCents: number;
    if (isReverseCharge) {
      lineVatOwedCents = Math.round((b.netCents * localVatRate) / 100);
    } else if (localTaxScheme === 'standard') {
      lineVatOwedCents = b.vatOwedCents;
    } else {
      lineVatOwedCents = 0;
    }
    return {
      ...b,
      vatOwedCents: lineVatOwedCents,
      vatDeductibleCents: effectiveVatDeductible ? lineVatOwedCents : 0,
    };
  });
}
